import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, LineChart, Line, CartesianGrid } from "recharts";
import api, { downloadFile } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Loader, Btn, EmptyState, Field, TextInput, SelectInput } from "../components/Primitives";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";

const PERSONA_META = {
  ceo:                { label: "CEO / Board",           icon: "Crown",          color: "var(--primary)" },
  compliance_officer: { label: "Compliance Officer",    icon: "ShieldCheck",    color: "var(--primary)" },
  department_head:    { label: "Department Head",       icon: "Users",          color: "var(--success)" },
  auditor:            { label: "Auditor",               icon: "ClipboardCheck", color: "var(--warn)" },
  it_security:        { label: "IT / Security",         icon: "Shield",         color: "var(--danger)" },
  legal:              { label: "Legal",                 icon: "Scale",          color: "var(--warn)" },
  vendor_management:  { label: "Vendor Management",     icon: "Truck",          color: "var(--primary)" },
};

const REPORT_META = {
  dpdp_readiness:        { label: "DPDP readiness",         icon: "ShieldCheck" },
  data_inventory:        { label: "Data inventory",         icon: "Database" },
  consent:               { label: "Consent",                 icon: "CheckSquare" },
  dsar:                  { label: "Rights request (DSAR)",   icon: "Users" },
  vendor_risk:           { label: "Vendor risk",              icon: "Truck" },
  incident:              { label: "Incident",                 icon: "AlertOctagon" },
  audit_evidence:        { label: "Audit evidence",           icon: "FolderLock" },
  board_summary:         { label: "Board summary",            icon: "BarChart3" },
  gap_analysis:          { label: "Gap analysis",             icon: "Search" },
  department_compliance: { label: "Department compliance",    icon: "Building2" },
};

const FORMAT_META = {
  pdf:  { label: "PDF",   icon: "FileText" },
  xlsx: { label: "Excel", icon: "Table" },
  docx: { label: "Word",  icon: "FileType" },
  csv:  { label: "CSV",   icon: "FileSpreadsheet" },
};

function StatCard({ label, value, tone = "primary", testId, hint, icon }) {
  const c = tone === "danger" ? "var(--danger)" : tone === "success" ? "var(--success)"
          : tone === "warn" ? "var(--warn)" : "var(--primary)";
  const Icon = icon ? (Icons[icon] || Icons.Circle) : null;
  return (
    <div className="surface rounded-xl p-4" style={{ border: "1px solid var(--border)" }}>
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider muted">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div data-testid={testId} className="text-2xl font-semibold mt-1" style={{ color: c }}>{value}</div>
      {hint && <div className="text-[11px] muted mt-1">{hint}</div>}
    </div>
  );
}

const emptySchedule = {
  name: "", report_type: "dpdp_readiness", format: "pdf", frequency: "weekly",
  filters: {}, recipient_emails: [], recipient_roles: ["dpo", "org_admin"], enabled: true,
};

export default function Reports() {
  const [tab, setTab] = useState("dashboards");
  const [persona, setPersona] = useState("ceo");
  const [dash, setDash] = useState(null);
  const [meta, setMeta] = useState({ personas: [], report_types: [], formats: [], frequencies: [], filter_fields: [] });
  const [history, setHistory] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);

  const [genOpen, setGenOpen] = useState(false);
  const [genReport, setGenReport] = useState("dpdp_readiness");
  const [genFormat, setGenFormat] = useState("pdf");
  const [genFilters, setGenFilters] = useState({});

  const [schedOpen, setSchedOpen] = useState(false);
  const [schedForm, setSchedForm] = useState(emptySchedule);
  const [schedEditing, setSchedEditing] = useState(null);

  const loadMeta = async () => {
    try { const { data } = await api.get("/reporting-meta/config"); setMeta(data); }
    catch { toast.error("Failed to load config"); }
  };
  const loadDashboard = async (p) => {
    setLoading(true);
    try { const { data } = await api.get(`/reporting/dashboards/${p}`); setDash(data); }
    catch { toast.error("Failed to load dashboard"); }
    setLoading(false);
  };
  const loadHistory = async () => {
    try { const { data } = await api.get("/reporting/reports/history"); setHistory(data.items || []); }
    catch { /* nop */ }
  };
  const loadSchedules = async () => {
    try { const { data } = await api.get("/reporting/schedules"); setSchedules(data.items || []); }
    catch { /* nop */ }
  };

  useEffect(() => { loadMeta(); loadDashboard(persona); loadHistory(); loadSchedules(); }, []);
  useEffect(() => { if (tab === "dashboards") loadDashboard(persona);   }, [persona]);
  useEffect(() => { if (tab === "history") loadHistory(); if (tab === "schedules") loadSchedules(); }, [tab]);

  const generateReport = async () => {
    toast.info("Generating…");
    try {
      const { data } = await api.post("/reporting/reports/generate",
                                        { report_type: genReport, format: genFormat, filters: genFilters });
      toast.success("Report ready");
      setGenOpen(false);
      downloadFile(`/reporting/reports/${data.id}/download`);
      loadHistory();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const deleteReport = async (rid) => {
    if (!window.confirm("Delete this report?")) return;
    try { await api.delete(`/reporting/reports/${rid}`); toast.success("Deleted"); loadHistory(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const saveSchedule = async () => {
    try {
      if (schedEditing) { await api.put(`/reporting/schedules/${schedEditing}`, schedForm); toast.success("Schedule updated"); }
      else { await api.post("/reporting/schedules", schedForm); toast.success("Schedule created"); }
      setSchedOpen(false); setSchedForm(emptySchedule); setSchedEditing(null);
      loadSchedules();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const deleteSchedule = async (sid) => {
    if (!window.confirm("Delete this schedule?")) return;
    try { await api.delete(`/reporting/schedules/${sid}`); toast.success("Deleted"); loadSchedules(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const runNow = async (sid) => {
    toast.info("Running…");
    try {
      const { data } = await api.post(`/reporting/schedules/${sid}/run-now`);
      toast.success("Report generated");
      downloadFile(`/reporting/reports/${data.report_id}/download`);
      loadSchedules(); loadHistory();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const toggleEnabled = async (s) => {
    try { await api.put(`/reporting/schedules/${s.id}`, { enabled: !s.enabled }); loadSchedules(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const openEditSchedule = (s) => {
    setSchedForm({ ...emptySchedule, ...s, recipient_emails: s.recipient_emails || [], recipient_roles: s.recipient_roles || [] });
    setSchedEditing(s.id); setSchedOpen(true);
  };

  const downloadUnifiedExecutive = async () => {
    const genId = toast.info("Generating unified executive report…");
    try {
      const res = await api.get("/reporting/unified-executive.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = "unified_executive_report.pdf"; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.dismiss(genId);
      toast.success("Report downloaded");
    } catch (e) { toast.dismiss(genId); toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const pd = dash?.persona_data || {};

  return (
    <div>
      <PageHeader
        title="Reports & Analytics"
        kicker="Executive dashboards · 10 report types · 4 formats · Unified board report · Scheduled delivery"
        description="Persona-based dashboards, on-demand and scheduled reports. Every report exportable to PDF, Excel, Word or CSV."
        testId="reports-page-header"
        actions={
          <>
            <Btn variant="secondary" onClick={() => { loadDashboard(persona); loadHistory(); loadSchedules(); }} data-testid="rep-refresh">
              <Icons.RefreshCw className="w-3.5 h-3.5" /> Refresh
            </Btn>
            <Btn
              variant="secondary"
              onClick={downloadUnifiedExecutive}
              data-testid="rep-unified-executive"
              title="Board-ready PDF combining DPDP + VAPT + ISO 27001"
            >
              <Icons.FileBarChart2 className="w-3.5 h-3.5" /> Unified Executive Report
            </Btn>
            <Btn onClick={() => { setGenReport("dpdp_readiness"); setGenFormat("pdf"); setGenFilters({}); setGenOpen(true); }} data-testid="rep-generate">
              <Icons.Download className="w-3.5 h-3.5" /> Generate report
            </Btn>
          </>
        }
      />

      <div className="mb-5 flex items-center gap-1 surface rounded-lg p-1 w-fit" style={{ border: "1px solid var(--border)" }}>
        {[
          { key: "dashboards", label: "Dashboards", icon: "LayoutDashboard" },
          { key: "generate", label: "Reports", icon: "FileText" },
          { key: "schedules", label: "Schedules", icon: "Calendar" },
          { key: "history", label: "History", icon: "History" },
        ].map(t => {
          const Icon = Icons[t.icon] || Icons.Circle;
          return (
            <button key={t.key} data-testid={`rep-tab-${t.key}`} onClick={() => setTab(t.key)}
              className="px-3 py-1.5 text-xs font-medium rounded-md flex items-center gap-1.5"
              style={{ background: tab === t.key ? "var(--primary-soft)" : "transparent", color: tab === t.key ? "var(--primary)" : "var(--muted)" }}>
              <Icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "dashboards" && (
        <div>
          <div className="mb-5 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="persona-switcher">
            {Object.entries(PERSONA_META).map(([k, p]) => {
              const Icon = Icons[p.icon] || Icons.User;
              const active = persona === k;
              return (
                <button key={k} onClick={() => setPersona(k)} data-testid={`persona-${k}`}
                  className="p-2.5 rounded-md text-left transition-all"
                  style={{ background: active ? "var(--primary-soft)" : "var(--bg-2)", border: `1px solid ${active ? "var(--primary)" : "var(--border)"}` }}>
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded flex items-center justify-center" style={{ background: active ? "var(--primary)" : "var(--bg-1)", color: active ? "white" : p.color }}>
                      <Icon className="w-3.5 h-3.5" />
                    </div>
                    <div className="text-[11.5px] font-medium ink truncate">{p.label}</div>
                  </div>
                </button>
              );
            })}
          </div>

          {loading || !dash ? <Loader /> : (
            <div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                <StatCard label="Compliance score" value={`${dash.compliance_score}%`} tone={dash.compliance_score >= 80 ? "success" : dash.compliance_score >= 50 ? "warn" : "danger"} icon="Target" testId="kpi-score" />
                <StatCard label="Implementation" value={`${dash.implementation_pct}%`} hint={`${dash.total_controls} controls`} icon="ShieldCheck" testId="kpi-impl" />
                <StatCard label="High-risk open" value={dash.high_risk_open} tone={dash.high_risk_open > 0 ? "danger" : "success"} icon="AlertTriangle" testId="kpi-hro" />
                <StatCard label="Open findings" value={dash.open_findings} tone={dash.open_findings > 0 ? "warn" : "success"} icon="Flag" testId="kpi-findings" />
                <StatCard label="Consent coverage" value={`${dash.consent_coverage_pct}%`} hint={`${dash.consent_active}/${dash.consent_total}`} icon="CheckSquare" testId="kpi-consent" />
                <StatCard label="Data inventory" value={`${dash.inventory_completion_pct}%`} hint={`${dash.total_assets} assets`} icon="Database" testId="kpi-inv" />
                <StatCard label="Vendor risk (high)" value={dash.high_risk_vendors} tone={dash.high_risk_vendors > 0 ? "warn" : "success"} hint={`of ${dash.total_vendors}`} icon="Truck" testId="kpi-vendors" />
                <StatCard label="DSAR SLA breach" value={dash.dsar_breached} tone={dash.dsar_breached > 0 ? "danger" : "success"} hint={`${dash.dsar_open} open · ${dash.dsar_at_risk} at-risk`} icon="Users" testId="kpi-dsar" />
              </div>

              <PersonaPanels persona={persona} pd={pd} />

              <div className="surface rounded-xl p-5 mt-5" style={{ border: "1px solid var(--border)" }}>
                <div className="text-[13px] font-semibold ink mb-3 flex items-center gap-2">
                  <Icons.Calendar className="w-4 h-4" style={{ color: "var(--warn)" }} />
                  Upcoming compliance deadlines
                </div>
                <div className="space-y-1.5" data-testid="deadlines">
                  {(dash.upcoming_deadlines || []).length === 0 ? <div className="text-[12px] muted">Nothing due in the next 30 days.</div>
                    : dash.upcoming_deadlines.map(d => (
                      <a key={d.id + d.type} href={d.link} className="p-2 rounded-md flex items-center gap-2 hover:bg-app" style={{ background: "var(--bg-2)" }}>
                        <Icons.CalendarClock className="w-3.5 h-3.5" style={{ color: d.days < 0 ? "var(--danger)" : "var(--warn)" }} />
                        <div className="text-[11.5px] ink flex-1 truncate">{d.label}</div>
                        <div className="text-[10.5px] muted uppercase tracking-wider">{d.type.replace(/_/g, " ")}</div>
                        <div className="text-[11px] font-semibold" style={{ color: d.days < 0 ? "var(--danger)" : "var(--warn)" }}>
                          {d.days < 0 ? `${-d.days}d overdue` : `in ${d.days}d`}
                        </div>
                      </a>
                    ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "generate" && (
        <div>
          <div className="text-[12px] muted mb-3">Choose a report type and format. All 10 types can be exported in any of 4 formats.</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="report-cards">
            {Object.entries(REPORT_META).map(([k, r]) => {
              const Icon = Icons[r.icon] || Icons.FileText;
              return (
                <div key={k} className="surface rounded-xl p-4" style={{ border: "1px solid var(--border)" }} data-testid={`report-card-${k}`}>
                  <div className="flex items-start gap-2 mb-3">
                    <div className="w-9 h-9 rounded-md flex items-center justify-center shrink-0" style={{ background: "var(--primary-soft)", color: "var(--primary)" }}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1"><div className="text-[13px] font-semibold ink">{r.label}</div></div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {Object.entries(FORMAT_META).map(([f, fm]) => {
                      const FIcon = Icons[fm.icon] || Icons.File;
                      return (
                        <button key={f} onClick={() => { setGenReport(k); setGenFormat(f); setGenFilters({}); setGenOpen(true); }} data-testid={`gen-${k}-${f}`}
                          className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded"
                          style={{ background: "var(--bg-2)", color: "var(--ink)" }}>
                          <FIcon className="w-3 h-3" /> {fm.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === "schedules" && (
        <div>
          <div className="mb-4 flex justify-end">
            <Btn size="sm" onClick={() => { setSchedForm(emptySchedule); setSchedEditing(null); setSchedOpen(true); }} data-testid="sched-new">
              <Icons.Plus className="w-3 h-3" /> New schedule
            </Btn>
          </div>
          {schedules.length === 0 ? <EmptyState title="No schedules" hint="Create your first automated report schedule." />
            : (
              <div className="surface rounded-xl overflow-hidden" style={{ border: "1px solid var(--border)" }}>
                <table className="w-full text-sm">
                  <thead style={{ background: "var(--bg-2)" }}>
                    <tr>{["Name", "Report", "Format", "Freq", "Recipients", "Next", "Last", "Status", ""].map(h => (
                      <th key={h} className="text-left px-3 py-2 text-[10.5px] font-semibold uppercase tracking-wider muted">{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody data-testid="schedules-tbody">
                    {schedules.map(s => (
                      <tr key={s.id} style={{ borderTop: "1px solid var(--border)" }} data-testid={`sched-row-${s.id}`}>
                        <td className="px-3 py-2 text-[11.5px] ink font-medium">{s.name}</td>
                        <td className="px-3 py-2 text-[11px] muted">{REPORT_META[s.report_type]?.label || s.report_type}</td>
                        <td className="px-3 py-2 text-[11px] muted uppercase">{s.format}</td>
                        <td className="px-3 py-2 text-[11px] muted capitalize">{s.frequency}</td>
                        <td className="px-3 py-2 text-[10.5px] muted">{(s.recipient_emails || []).length}e · {(s.recipient_roles || []).length}r</td>
                        <td className="px-3 py-2 text-[10.5px] muted">{s.next_run_at?.slice(0, 10)}</td>
                        <td className="px-3 py-2 text-[10.5px] muted">{s.last_run_at?.slice(0, 10) || "—"}</td>
                        <td className="px-3 py-2">
                          <button onClick={() => toggleEnabled(s)} data-testid={`sched-toggle-${s.id}`}
                            className="text-[10.5px] font-medium px-1.5 py-[2px] rounded"
                            style={{ background: s.enabled ? "var(--success-soft)" : "var(--bg-2)", color: s.enabled ? "var(--success)" : "var(--muted)" }}>
                            {s.enabled ? "enabled" : "disabled"}
                          </button>
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <button onClick={() => runNow(s.id)} className="text-[11px] font-medium hover:underline mr-2" style={{ color: "var(--primary)" }} data-testid={`sched-run-${s.id}`}>Run now</button>
                          <button onClick={() => openEditSchedule(s)} className="text-[11px] font-medium hover:underline mr-2" style={{ color: "var(--primary)" }} data-testid={`sched-edit-${s.id}`}>Edit</button>
                          <button onClick={() => deleteSchedule(s.id)} className="text-[11px] font-medium hover:underline" style={{ color: "var(--danger)" }} data-testid={`sched-del-${s.id}`}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </div>
      )}

      {tab === "history" && (
        history.length === 0 ? <EmptyState title="No reports yet" hint="Generate one from the Reports tab." />
          : (
            <div className="surface rounded-xl overflow-hidden" style={{ border: "1px solid var(--border)" }}>
              <table className="w-full text-sm">
                <thead style={{ background: "var(--bg-2)" }}>
                  <tr>{["Title", "Type", "Format", "Size", "Generated", "By", ""].map(h => (
                    <th key={h} className="text-left px-3 py-2 text-[10.5px] font-semibold uppercase tracking-wider muted">{h}</th>
                  ))}</tr>
                </thead>
                <tbody data-testid="history-tbody">
                  {history.map(r => (
                    <tr key={r.id} style={{ borderTop: "1px solid var(--border)" }} data-testid={`hist-row-${r.id}`}>
                      <td className="px-3 py-2 text-[11.5px] ink font-medium">{r.title}</td>
                      <td className="px-3 py-2 text-[11px] muted">{REPORT_META[r.report_type]?.label || r.report_type}</td>
                      <td className="px-3 py-2 text-[11px] muted uppercase">{r.format}</td>
                      <td className="px-3 py-2 text-[11px] muted">{(r.size / 1024).toFixed(1)} KB</td>
                      <td className="px-3 py-2 text-[10.5px] muted">{r.created_at?.slice(0, 19).replace("T", " ")}</td>
                      <td className="px-3 py-2 text-[11px] muted truncate max-w-[160px]">{r.generated_by}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button onClick={() => downloadFile(`/reporting/reports/${r.id}/download`)} className="text-[11px] font-medium hover:underline mr-2" style={{ color: "var(--primary)" }} data-testid={`hist-dl-${r.id}`}>Download</button>
                        <button onClick={() => deleteReport(r.id)} className="text-[11px] font-medium hover:underline" style={{ color: "var(--danger)" }} data-testid={`hist-del-${r.id}`}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
      )}

      {/* GENERATE MODAL */}
      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Generate report</DialogTitle>
            <DialogDescription>{REPORT_META[genReport]?.label} → {FORMAT_META[genFormat]?.label}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Report type">
              <SelectInput data-testid="gen-type" value={genReport} onChange={(e) => setGenReport(e.target.value)}>
                {Object.entries(REPORT_META).map(([k, r]) => <option key={k} value={k}>{r.label}</option>)}
              </SelectInput>
            </Field>
            <Field label="Format">
              <SelectInput data-testid="gen-format" value={genFormat} onChange={(e) => setGenFormat(e.target.value)}>
                {meta.formats.map(f => <option key={f} value={f}>{FORMAT_META[f]?.label || f}</option>)}
              </SelectInput>
            </Field>
            <Field label="Date from"><TextInput type="date" data-testid="gen-date-from" value={genFilters.date_from || ""} onChange={(e) => setGenFilters({ ...genFilters, date_from: e.target.value })} /></Field>
            <Field label="Date to"><TextInput type="date" data-testid="gen-date-to" value={genFilters.date_to || ""} onChange={(e) => setGenFilters({ ...genFilters, date_to: e.target.value })} /></Field>
            <Field label="Department"><TextInput data-testid="gen-dept" value={genFilters.department || ""} onChange={(e) => setGenFilters({ ...genFilters, department: e.target.value })} /></Field>
            <Field label="Risk rating">
              <SelectInput data-testid="gen-risk" value={genFilters.risk_rating || ""} onChange={(e) => setGenFilters({ ...genFilters, risk_rating: e.target.value })}>
                <option value="">All</option><option value="low">low</option><option value="medium">medium</option><option value="high">high</option><option value="critical">critical</option>
              </SelectInput>
            </Field>
            <Field label="Status"><TextInput data-testid="gen-status" value={genFilters.status || ""} onChange={(e) => setGenFilters({ ...genFilters, status: e.target.value })} /></Field>
            <Field label="Regulation">
              <SelectInput data-testid="gen-reg" value={genFilters.regulation || ""} onChange={(e) => setGenFilters({ ...genFilters, regulation: e.target.value })}>
                <option value="">All</option>
                {["DPDP Act", "DPDP Rules 2025", "ISO 27001", "SOC 2", "GDPR", "Internal Policy"].map(r => <option key={r} value={r}>{r}</option>)}
              </SelectInput>
            </Field>
          </div>
          <DialogFooter>
            <Btn variant="secondary" onClick={() => setGenOpen(false)}>Cancel</Btn>
            <Btn onClick={generateReport} data-testid="gen-run"><Icons.Download className="w-3.5 h-3.5" /> Generate + download</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SCHEDULE MODAL */}
      <Dialog open={schedOpen} onOpenChange={setSchedOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{schedEditing ? "Edit schedule" : "New schedule"}</DialogTitle>
            <DialogDescription>Automatically generate + deliver a report on a recurring schedule.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 max-h-[65vh] overflow-y-auto">
            <Field label="Name" required className="col-span-2"><TextInput data-testid="sch-name" value={schedForm.name} onChange={(e) => setSchedForm({ ...schedForm, name: e.target.value })} placeholder="e.g., Weekly DPDP readiness for board" /></Field>
            <Field label="Report type">
              <SelectInput data-testid="sch-type" value={schedForm.report_type} onChange={(e) => setSchedForm({ ...schedForm, report_type: e.target.value })}>
                {Object.entries(REPORT_META).map(([k, r]) => <option key={k} value={k}>{r.label}</option>)}
              </SelectInput>
            </Field>
            <Field label="Format">
              <SelectInput data-testid="sch-format" value={schedForm.format} onChange={(e) => setSchedForm({ ...schedForm, format: e.target.value })}>
                {Object.entries(FORMAT_META).map(([k, f]) => <option key={k} value={k}>{f.label}</option>)}
              </SelectInput>
            </Field>
            <Field label="Frequency">
              <SelectInput data-testid="sch-freq" value={schedForm.frequency} onChange={(e) => setSchedForm({ ...schedForm, frequency: e.target.value })}>
                {(meta.frequencies || []).map(f => <option key={f} value={f}>{f}</option>)}
              </SelectInput>
            </Field>
            <Field label="Enabled">
              <SelectInput value={schedForm.enabled ? "1" : "0"} onChange={(e) => setSchedForm({ ...schedForm, enabled: e.target.value === "1" })}>
                <option value="1">enabled</option><option value="0">disabled</option>
              </SelectInput>
            </Field>
            <Field label="Recipient emails (comma-separated)" className="col-span-2">
              <TextInput data-testid="sch-emails" value={(schedForm.recipient_emails || []).join(", ")}
                onChange={(e) => setSchedForm({ ...schedForm, recipient_emails: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} />
            </Field>
            <Field label="Recipient roles" className="col-span-2">
              <div className="flex flex-wrap gap-1.5">
                {["dpo", "org_admin", "legal_reviewer", "auditor", "security_officer", "business_owner"].map(r => {
                  const on = (schedForm.recipient_roles || []).includes(r);
                  return (
                    <button key={r} type="button" onClick={() => setSchedForm(s => ({
                      ...s, recipient_roles: on ? s.recipient_roles.filter(x => x !== r) : [...(s.recipient_roles || []), r],
                    }))} className="px-2 py-1 text-[11px] rounded"
                       style={{ background: on ? "var(--primary-soft)" : "var(--bg-2)", color: on ? "var(--primary)" : "var(--muted)" }}
                       data-testid={`sch-role-${r}`}>{r}</button>
                  );
                })}
              </div>
            </Field>
          </div>
          <DialogFooter>
            <Btn variant="secondary" onClick={() => setSchedOpen(false)}>Cancel</Btn>
            <Btn onClick={saveSchedule} disabled={!schedForm.name} data-testid="sch-save">{schedEditing ? "Update" : "Create"}</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PersonaPanels({ persona, pd }) {
  const CHART_COLORS = ["#6366F1", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#06B6D4", "#EC4899", "#84CC16"];
  if (persona === "ceo") {
    return (
      <div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
            <div className="text-[13px] font-semibold ink mb-3 flex items-center gap-2"><Icons.TrendingUp className="w-3.5 h-3.5" style={{ color: "var(--primary)" }} /> Executive KPIs</div>
            <div className="grid grid-cols-2 gap-3" data-testid="ceo-top-kpis">
              {(pd.top_kpis || []).map((k, i) => (
                <div key={i} className="p-3 rounded-md" style={{ background: k.tone === "danger" ? "var(--danger-soft)" : k.tone === "warn" ? "var(--warn-soft)" : "var(--success-soft)" }}>
                  <div className="text-[10.5px] font-semibold uppercase tracking-wider muted">{k.label}</div>
                  <div className="text-2xl font-semibold" style={{ color: k.tone === "danger" ? "var(--danger)" : k.tone === "warn" ? "var(--warn)" : "var(--success)" }}>{k.value}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
            <div className="text-[13px] font-semibold ink mb-3">Top open high-risk gaps</div>
            <div className="space-y-1.5" data-testid="ceo-top-gaps">
              {(pd.top_open_gaps || []).length === 0 ? <div className="text-[12px] muted">No high-risk gaps. 🎯</div>
                : pd.top_open_gaps.map(g => (
                  <a key={g.id} href="/app/compliance-controls" className="p-2 rounded-md flex items-center gap-2 hover:bg-app" style={{ background: "var(--bg-2)" }}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--danger)" }} />
                    <span className="text-[11px] mono muted">{g.control_id}</span>
                    <span className="text-[11.5px] ink flex-1 truncate">{g.name}</span>
                    <span className="text-[10px] uppercase font-semibold" style={{ color: "var(--danger)" }}>{g.risk}</span>
                  </a>
                ))}
            </div>
          </div>
        </div>
        {(pd.trend || []).length > 0 && (
          <div className="surface rounded-xl p-5 mt-5" style={{ border: "1px solid var(--border)" }}>
            <div className="text-[13px] font-semibold ink mb-3">Compliance activity trend</div>
            <div style={{ width: "100%", height: 200 }} data-testid="ceo-trend-chart">
              <ResponsiveContainer>
                <LineChart data={pd.trend}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={{ fontSize: "11px" }} />
                  <Line type="monotone" dataKey="count" stroke="#6366F1" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    );
  }
  if (persona === "compliance_officer") {
    const byStatusData = Object.entries(pd.controls_by_status || {})
      .filter(([, v]) => v > 0).map(([k, v]) => ({ name: k.replace(/_/g, " "), value: v }));
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
          <div className="text-[13px] font-semibold ink mb-3">Controls by status</div>
          {byStatusData.length > 0 ? (
            <div style={{ width: "100%", height: 200 }} data-testid="co-status-chart">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={byStatusData} cx="50%" cy="50%" innerRadius={40} outerRadius={70} dataKey="value" label={{ fontSize: 10 }}>
                    {byStatusData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: "11px" }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : <div className="text-[12px] muted">No controls yet.</div>}
          <div className="space-y-1 mt-2" data-testid="co-by-status">
            {byStatusData.map((s, i) => (
              <div key={s.name} className="flex items-center gap-2 text-[10.5px]">
                <span className="w-2 h-2 rounded-full" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                <span className="capitalize flex-1 ink">{s.name}</span><span className="muted">{s.value}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
          <div className="text-[13px] font-semibold ink mb-3">Attention</div>
          <div className="space-y-2" data-testid="co-attention">
            <div className="flex justify-between text-[12px] p-2 rounded-md" style={{ background: "var(--warn-soft)" }}><span>Pending approvals</span><b style={{ color: "var(--warn)" }}>{pd.pending_approvals}</b></div>
            <div className="flex justify-between text-[12px] p-2 rounded-md" style={{ background: "var(--warn-soft)" }}><span>Overdue reviews</span><b style={{ color: "var(--warn)" }}>{pd.overdue_reviews}</b></div>
            <div className="flex justify-between text-[12px] p-2 rounded-md" style={{ background: "var(--danger-soft)" }}><span>Overdue evidence</span><b style={{ color: "var(--danger)" }}>{pd.overdue_evidence}</b></div>
          </div>
        </div>
        <div className="surface rounded-xl p-5 lg:col-span-2" style={{ border: "1px solid var(--border)" }}>
          <div className="text-[13px] font-semibold ink mb-3">By department & regulation</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider muted mb-2">Department</div>
              {(pd.by_department || []).length === 0 ? <div className="text-[12px] muted">No department tags</div>
                : (
                  <div style={{ width: "100%", height: 160 }} data-testid="co-dept-chart">
                    <ResponsiveContainer>
                      <BarChart data={pd.by_department} layout="vertical">
                        <XAxis type="number" tick={{ fontSize: 9 }} />
                        <YAxis type="category" dataKey="department" tick={{ fontSize: 9 }} width={70} />
                        <Tooltip contentStyle={{ fontSize: "11px" }} />
                        <Bar dataKey="implemented" stackId="a" fill="#10B981" />
                        <Bar dataKey="total" stackId="b" fill="#E5E7EB" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
            </div>
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider muted mb-2">Regulation</div>
              {(pd.by_regulation || []).length === 0 ? <div className="text-[12px] muted">No regulation mapping</div>
                : (
                  <div style={{ width: "100%", height: 160 }} data-testid="co-reg-chart">
                    <ResponsiveContainer>
                      <BarChart data={pd.by_regulation} layout="vertical">
                        <XAxis type="number" tick={{ fontSize: 9 }} />
                        <YAxis type="category" dataKey="regulation" tick={{ fontSize: 9 }} width={80} />
                        <Tooltip contentStyle={{ fontSize: "11px" }} />
                        <Bar dataKey="implemented" stackId="a" fill="#6366F1" />
                        <Bar dataKey="total" stackId="b" fill="#E5E7EB" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (persona === "it_security") {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
          <div className="text-[13px] font-semibold ink mb-3">Systems & integrations</div>
          <div className="space-y-2" data-testid="it-systems">
            <div className="flex justify-between text-[12px] p-2 rounded-md" style={{ background: "var(--bg-2)" }}><span>Connected systems</span><b>{pd.connected_systems}</b></div>
            <div className="flex justify-between text-[12px] p-2 rounded-md" style={{ background: "var(--success-soft)" }}><span>Active connectors</span><b style={{ color: "var(--success)" }}>{pd.active_connectors}</b></div>
            <div className="flex justify-between text-[12px] p-2 rounded-md" style={{ background: "var(--danger-soft)" }}><span>Failed / sync failures</span><b style={{ color: "var(--danger)" }}>{pd.failed_connectors}</b></div>
            <div className="flex justify-between text-[12px] p-2 rounded-md" style={{ background: "var(--primary-soft)" }}><span>Integration health</span><b style={{ color: "var(--primary)" }}>{pd.integration_health_pct}%</b></div>
          </div>
        </div>
        <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
          <div className="text-[13px] font-semibold ink mb-3">Security posture</div>
          <div className="space-y-2" data-testid="it-security">
            <div className="flex justify-between text-[12px] p-2 rounded-md" style={{ background: "var(--danger-soft)" }}><span>Open security incidents</span><b style={{ color: "var(--danger)" }}>{pd.security_incidents}</b></div>
            <div className="flex justify-between text-[12px] p-2 rounded-md" style={{ background: "var(--danger-soft)" }}><span>Critical incidents</span><b style={{ color: "var(--danger)" }}>{pd.critical_incidents}</b></div>
            <div className="flex justify-between text-[12px] p-2 rounded-md" style={{ background: "var(--warn-soft)" }}><span>Access control gaps</span><b style={{ color: "var(--warn)" }}>{pd.access_control_gaps}</b></div>
            <div className="flex justify-between text-[12px] p-2 rounded-md" style={{ background: "var(--bg-2)" }}><span>Discovery — mapped/total</span><b>{pd.data_discovery_status?.mapped} / {pd.data_discovery_status?.discovered}</b></div>
          </div>
        </div>
      </div>
    );
  }
  if (persona === "auditor") {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
          <div className="text-[13px] font-semibold ink mb-3">Evidence status</div>
          <div className="space-y-2" data-testid="au-evidence">
            <div className="flex justify-between text-[12px] p-2 rounded-md" style={{ background: "var(--bg-2)" }}><span>Total evidence</span><b>{pd.total_evidence}</b></div>
            <div className="flex justify-between text-[12px] p-2 rounded-md" style={{ background: "var(--success-soft)" }}><span>Approved</span><b style={{ color: "var(--success)" }}>{pd.approved_evidence}</b></div>
            <div className="flex justify-between text-[12px] p-2 rounded-md" style={{ background: "var(--warn-soft)" }}><span>Pending review</span><b style={{ color: "var(--warn)" }}>{pd.pending_evidence}</b></div>
            <div className="flex justify-between text-[12px] p-2 rounded-md" style={{ background: "var(--danger-soft)" }}><span>Controls without evidence</span><b style={{ color: "var(--danger)" }}>{pd.controls_with_no_evidence}</b></div>
          </div>
        </div>
        <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
          <div className="text-[13px] font-semibold ink mb-3">Audit readiness</div>
          <div className="text-4xl font-semibold" style={{ color: "var(--primary)" }} data-testid="au-readiness">{pd.audit_readiness_pct}%</div>
          <div className="text-[11px] muted mt-1">Open audits: {pd.open_audits} / {pd.total_audits}</div>
          <div className="h-2 rounded-full overflow-hidden mt-3" style={{ background: "var(--bg-2)" }}>
            <div className="h-full" style={{ width: `${pd.audit_readiness_pct}%`, background: "var(--primary)" }} />
          </div>
        </div>
      </div>
    );
  }
  if (persona === "vendor_management") {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
          <div className="text-[13px] font-semibold ink mb-3">Vendor risk distribution</div>
          <div className="space-y-2" data-testid="vm-risk">
            {Object.entries(pd.by_risk || {}).map(([k, v]) => (
              <div key={k} className="flex justify-between text-[12px] p-2 rounded-md" style={{ background: "var(--bg-2)" }}>
                <span className="capitalize">{k}</span><b style={{ color: k === "critical" || k === "high" ? "var(--danger)" : k === "medium" ? "var(--warn)" : "var(--muted)" }}>{v}</b>
              </div>
            ))}
            <div className="flex justify-between text-[12px] p-2 rounded-md" style={{ background: "var(--warn-soft)" }}><span>Docs expiring in 30 days</span><b style={{ color: "var(--warn)" }}>{pd.expiring_documents_30d}</b></div>
          </div>
        </div>
        <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
          <div className="text-[13px] font-semibold ink mb-3">Top high-risk vendors</div>
          <div className="space-y-1.5" data-testid="vm-top">
            {(pd.top_high_risk || []).length === 0 ? <div className="text-[12px] muted">No high-risk vendors. ✓</div>
              : pd.top_high_risk.map(v => (
                <a key={v.id} href="/app/vendors" className="p-2 rounded-md flex items-center gap-2 hover:bg-app" style={{ background: "var(--bg-2)" }}>
                  <Icons.Truck className="w-3.5 h-3.5" style={{ color: "var(--danger)" }} />
                  <span className="text-[11.5px] ink flex-1 truncate">{v.name}</span>
                  <span className="text-[10px] uppercase font-semibold" style={{ color: "var(--danger)" }}>{v.risk}</span>
                </a>
              ))}
          </div>
        </div>
      </div>
    );
  }
  if (persona === "legal") {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
          <div className="text-[13px] font-semibold ink mb-3">Legal work queue</div>
          <div className="space-y-2" data-testid="legal-queue">
            <div className="flex justify-between text-[12px] p-2 rounded-md" style={{ background: "var(--warn-soft)" }}><span>Pending policy approvals</span><b style={{ color: "var(--warn)" }}>{pd.pending_approvals}</b></div>
            <div className="flex justify-between text-[12px] p-2 rounded-md" style={{ background: "var(--danger-soft)" }}><span>Breach notifications pending</span><b style={{ color: "var(--danger)" }}>{pd.breach_notification_pending}</b></div>
            <div className="flex justify-between text-[12px] p-2 rounded-md" style={{ background: "var(--danger-soft)" }}><span>DSAR SLA breach</span><b style={{ color: "var(--danger)" }}>{pd.dsar_breached}</b></div>
            <div className="flex justify-between text-[12px] p-2 rounded-md" style={{ background: "var(--bg-2)" }}><span>Cross-border transfers</span><b>{pd.cross_border_transfers}</b></div>
          </div>
        </div>
      </div>
    );
  }
  if (persona === "department_head") {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
          <div className="text-[13px] font-semibold ink mb-3">My department: {pd.department}</div>
          <div className="grid grid-cols-2 gap-3" data-testid="dh-stats">
            <div className="p-3 rounded-md" style={{ background: "var(--bg-2)" }}>
              <div className="text-[10.5px] uppercase tracking-wider muted">My controls</div>
              <div className="text-2xl font-semibold ink">{pd.my_controls}</div>
            </div>
            <div className="p-3 rounded-md" style={{ background: "var(--success-soft)" }}>
              <div className="text-[10.5px] uppercase tracking-wider muted">Implemented</div>
              <div className="text-2xl font-semibold" style={{ color: "var(--success)" }}>{pd.my_implemented}</div>
            </div>
            <div className="p-3 rounded-md col-span-2" style={{ background: "var(--warn-soft)" }}>
              <div className="text-[10.5px] uppercase tracking-wider muted">Open findings on my controls</div>
              <div className="text-2xl font-semibold" style={{ color: "var(--warn)" }}>{pd.my_open_findings}</div>
            </div>
          </div>
        </div>
        <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
          <div className="text-[13px] font-semibold ink mb-3">My top gaps</div>
          <div className="space-y-1.5" data-testid="dh-gaps">
            {(pd.top_gaps || []).length === 0 ? <div className="text-[12px] muted">No open gaps. 🎯</div>
              : pd.top_gaps.map(g => (
                <a key={g.id} href="/app/compliance-controls" className="p-2 rounded-md flex items-center gap-2 hover:bg-app" style={{ background: "var(--bg-2)" }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--warn)" }} />
                  <span className="text-[11.5px] ink flex-1 truncate">{g.name}</span>
                  <span className="text-[10px] uppercase muted">{g.status}</span>
                </a>
              ))}
          </div>
        </div>
      </div>
    );
  }
  return null;
}
